import type { User } from '@jemmi/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiRequest } from '@/lib/apiClient';

const AUTH_QUERY_KEY = ['auth', 'me'] as const;

type AuthData = { user: User };

type LoginInput = { email: string; password: string };
type RegisterInput = LoginInput & {
  displayName: string;
  acceptedTerms: true;
  acceptedPrivacy: true;
};

async function getCurrentUser(): Promise<AuthData | null> {
  try {
    return await apiRequest<AuthData>('/auth/me');
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useAuth() {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: getCurrentUser,
    retry: false,
  });

  const login = useMutation({
    mutationFn: (input: LoginInput) =>
      apiRequest<AuthData>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => queryClient.setQueryData(AUTH_QUERY_KEY, data),
  });

  const register = useMutation({
    mutationFn: (input: RegisterInput) =>
      apiRequest<AuthData>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => queryClient.setQueryData(AUTH_QUERY_KEY, data),
  });

  const logout = useMutation({
    mutationFn: () =>
      apiRequest<{ loggedOut: boolean }>('/auth/logout', {
        method: 'POST',
      }),
    onSettled: () => queryClient.setQueryData(AUTH_QUERY_KEY, null),
  });

  return {
    user: session.data?.user ?? null,
    isLoading: session.isLoading,
    isAuthenticated: Boolean(session.data?.user),
    sessionError: session.error,
    login,
    register,
    logout,
  };
}
